"use client"

import { memo, useCallback, useState } from "react"
import {
  MoreHorizontal,
  Play,
  Pause,
  Video,
  Music2,
} from "lucide-react"

import { useDispatch } from "react-redux"

import { changePlay } from "@/core/actions"
import { useMusicContext } from "@/context/musicContext"

import TrackContextMenu from "@/app/pages/playlistPage/components/TrackContextMenu"

import {
  getQueueSongArtist,
  getQueueSongId,
  getQueueSongTime,
  getQueueSongTitle,
  type QueueSong,
} from "./queue-utils"

type Props = {
  song: QueueSong
  index: number
  isCurrent: boolean
}

function QueueItem({
  song,
  index,
  isCurrent,
}: Props) {
  const dispatch = useDispatch()

  const { playSong } = useMusicContext()

  const [contextOpen, setContextOpen] =
    useState(false)

  const title = getQueueSongTitle(song)
  const artist = getQueueSongArtist(song)
  const time = getQueueSongTime(song)

  /*
   * =========================================================
   * PLAY
   * =========================================================
   */

  const handlePlay = useCallback(() => {
    playSong(
      {
        link: song.link,
        url: song.url,
        songName: song.songName,
        songArtist: song.songArtist,
        songimg: song.songimg,
      },
      index,
    )
  }, [
    playSong,
    song,
    index,
  ])

  /*
   * =========================================================
   * TOGGLE
   * =========================================================
   */

  const handleToggle = useCallback(() => {
    if (isCurrent) {
      dispatch(changePlay(false))
      return
    }

    handlePlay()
  }, [
    dispatch,
    handlePlay,
    isCurrent,
  ])

  /*
   * =========================================================
   * CONTEXT MENU
   * =========================================================
   */

  const handleContext = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault()
      event.stopPropagation()

      setContextOpen(true)
    },
    [],
  )

  /*
   * =========================================================
   * RENDER
   * =========================================================
   */

  return (
    <>
      <div
        className={`
          group
          relative
          flex
          w-full
          items-center
          gap-1
          rounded-lg
          px-0
          py-2
          transition-all
          duration-200         

          ${
            isCurrent
              ? "bg-white/[0.055]"
              : "hover:bg-white/[0.035]"
          }
        `}
      >

        {/* =================================================
            POSITION
        ================================================= */}

        <div
          className="
            flex
            w-5
            shrink-0
            items-center
            justify-center
            text-[10px]
            tabular-nums
            text-white/25
          "
        >
          {isCurrent ? (
            <div
              className="
                flex
                h-4
                w-4
                items-center
                justify-center
                rounded-full
                bg-white/[0.08]
              "
            >
              <span
                className="
                  h-1.5
                  w-1.5
                  animate-pulse
                  rounded-full
                  bg-[var(--primary-color)]
                  shadow-[0_0_8px_var(--primary-color)]
                "
              />
            </div>
          ) : (
            song.index ?? index + 1
          )}
        </div>

        {/* =================================================
            COVER
        ================================================= */}

        <button
          type="button"
          onClick={handleToggle}
          aria-label={
            isCurrent
              ? "Pausar canción"
              : `Reproducir ${title}`
          }
          className="
            relative
            h-11
            w-11
            shrink-0
            overflow-hidden
            rounded-lg
            bg-white/[0.045]
            outline-none
            transition
            focus-visible:ring-2
            focus-visible:ring-white/20
          "
        >

          {song.songimg ? (
            <img
              src={song.songimg}
              alt=""
              loading="lazy"
              className="
                h-full
                w-full
                object-cover
                transition-transform
                duration-300
                group-hover:scale-[1.04]
              "
            />
          ) : (
            <div
              className="
                flex
                h-full
                w-full
                items-center
                justify-center
              "
            >
              <Music2
                size={15}
                className="text-white/20"
              />
            </div>
          )}

          {/* VIDEO */}

          {song.videoUrl && (
            <div
              className="
                absolute
                right-1
                top-1
                flex
                h-4
                w-4
                items-center
                justify-center
                rounded-full
                bg-black/55
                backdrop-blur-sm
              "
            >
              <Video
                size={8}
                className="text-white/70"
              />
            </div>
          )}

          {/* PLAY OVERLAY */}

          <div
            className={`
              absolute
              inset-0
              flex
              items-center
              justify-center
              bg-black/45
              backdrop-blur-[1px]
              transition-opacity
              duration-200

              ${
                isCurrent
                  ? "opacity-100"
                  : "opacity-0 group-hover:opacity-100"
              }
            `}
          >
            {isCurrent ? (
              <Pause
                size={14}
                fill="currentColor"
                className="text-white"
              />
            ) : (
              <Play
                size={14}
                fill="currentColor"
                className="text-white"
              />
            )}
          </div>
        </button>

        {/* =================================================
            INFO
        ================================================= */}

        <button
          type="button"
          onClick={handleToggle}
          className="
            min-w-0
            flex-1
            text-left
            outline-none
          "
        >
          <div className="flex min-w-0 items-center gap-2">

            <p
              className={`
                min-w-0
                truncate
                text-[11px]
                font-semibold
                leading-tight

                ${
                  isCurrent
                    ? "text-[var(--primary-color)]"
                    : "text-white/80"
                }
              `}
            >
              {title}
            </p>

            {song.videoUrl && (
              <Video
                size={10}
                className="
                  shrink-0
                  text-white/25
                "
              />
            )}
          </div>

          <p
            className="
              mt-1
              truncate
              text-[9px]
              leading-tight
              text-white/30
            "
          >
            {artist}
          </p>
        </button>

        {/* =================================================
            TIME
        ================================================= */}

        <span
          className="
            hidden
            w-10
            shrink-0
            text-right
            text-[9px]
            tabular-nums
            text-white/25
            sm:block
          "
        >
          {time}
        </span>

        {/* =================================================
            MORE
        ================================================= */}

        <button
          type="button"
          aria-label={`Opciones de ${title}`}
          onPointerDown={(event) => {
            event.preventDefault()
            event.stopPropagation()
          }}
          onClick={handleContext}
          className="
            flex
            h-8
            w-8
            shrink-0
            items-center
            justify-center
            rounded-full
            text-white/25
            opacity-60
            transition-all
            hover:bg-white/[0.06]
            hover:text-white/70
            sm:opacity-0
            sm:group-hover:opacity-100
          "
        >
          <MoreHorizontal size={17} />
        </button>

        {/* =================================================
            ACTIVE GLOW
        ================================================= */}

        {isCurrent && (
          <div
            className="
              pointer-events-none
              absolute
              inset-0
              rounded-xl
              ring-1
              ring-inset
              ring-[var(--primary-color)]/10
            "
          />
        )}
      </div>

      {/* ===================================================
          CONTEXT MENU
      =================================================== */}

      {contextOpen && (
        <TrackContextMenu
          open
          song={{
            songName: title,
            songArtist: artist,
            songimg: song.songimg,
          }}
          isFavorite={false}
          isOfflineSaved={false}
          isSavingOffline={false}
          onPlay={() => {
            setContextOpen(false)
            handlePlay()
          }}
          onArtist={() => {
            setContextOpen(false)
          }}
          onPlaylist={() => {
            setContextOpen(false)
          }}
          onFavorite={() => {
            setContextOpen(false)
          }}
          onOffline={() => {
            setContextOpen(false)
          }}
          onClose={() => {
            setContextOpen(false)
          }}
        />
      )}
    </>
  )
}

export default memo(QueueItem)